/**
 * Cross-step sign-up wizard state.
 *
 * Held in sessionStorage so a parent who closes the tab mid-flow can
 * resume without re-entering anything. Keys are scoped under a single
 * `signup` namespace so a future "I'll get one" handoff (Screen 1) can
 * blow it away atomically.
 */

import { create } from 'zustand';

export type AgeBand = '5-7' | '8-10' | '11-12';

export interface SignupDraft {
  /** Step 3 — the three "saying yes" booleans. */
  consents: {
    parent: boolean;
    save: boolean;
    adult: boolean;
  };
  /** Step 4 — child display name (no real names nudged) + age band. */
  child: {
    displayName: string;
    ageBand: AgeBand;
  };
  /** Step 5 — chosen avatar slug, or null if skipped. */
  avatarSlug: string | null;
}

interface SignupState extends SignupDraft {
  setConsents: (next: Partial<SignupDraft['consents']>) => void;
  setChild: (next: Partial<SignupDraft['child']>) => void;
  setAvatar: (slug: string | null) => void;
  reset: () => void;
}

const STORAGE_KEY = 'maysterko.signup.draft';

const defaultDraft: SignupDraft = {
  consents: { parent: false, save: false, adult: false },
  child: { displayName: '', ageBand: '5-7' },
  avatarSlug: null,
};

function load(): SignupDraft {
  if (typeof sessionStorage === 'undefined') return defaultDraft;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultDraft;
    return { ...defaultDraft, ...(JSON.parse(raw) as Partial<SignupDraft>) };
  } catch {
    return defaultDraft;
  }
}

function persist(draft: SignupDraft): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Quota exceeded / private mode — sign-up still works, just no resume.
  }
}

export const useSignup = create<SignupState>((set, get) => ({
  ...load(),

  setConsents: (next) => {
    set((s) => {
      const merged = { ...s.consents, ...next };
      persist({ ...get(), consents: merged });
      return { consents: merged };
    });
  },
  setChild: (next) => {
    set((s) => {
      const merged = { ...s.child, ...next };
      persist({ ...get(), child: merged });
      return { child: merged };
    });
  },
  setAvatar: (slug) => {
    set(() => {
      persist({ ...get(), avatarSlug: slug });
      return { avatarSlug: slug };
    });
  },
  reset: () => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(STORAGE_KEY);
    set(defaultDraft);
  },
}));
